import { useState } from "react";
import { useEffect } from "react";
import { connect } from "react-redux";

import LowIcon from "@mui/icons-material/ArrowDownward";
import HighIcon from "@mui/icons-material/ArrowUpward";
import MidIcon from "@mui/icons-material/ImportExport";
import NegIcon from "@mui/icons-material/ThumbDown";
import NeutralIcon from "@mui/icons-material/ThumbsUpDown";
import PosIcon from "@mui/icons-material/ThumbUp";
import { Box, Grid } from "@mui/material";
import Divider from "@mui/material/Divider";
import { alpha, darken, lighten, useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";

import constants from "../../assets/constants";
import { capitalizeAllWords, getAgeFromDateString, isValidUUID } from "../../utils/helpers";
import { extractGraphDataFromArray } from "../graphing/graphUtils";
import OmniAvatar from "../ui/omniAvatar";
import Widget from "../ui/widget";
import WidgetAvatar from "../ui/widgetAvatar";
import WidgetContent from "../ui/widgetContent";
import WidgetSkeleton from "../ui/widgetSkeleton";
import WidgetTitle from "../ui/widgetTitle";

const statistical = require("statistical-js");

const PatientProfile = props => {
  const { patientODM, patientChart, patientMotivations } = props;

  const generateDepressionData = () => {
    const userInfo = patientChart;
    if (userInfo == null) {
      return null;
    }

    let depressionData = null;
    for (const [key] of Object.entries(userInfo.symptoms)) {
      if (key.toUpperCase().includes("DEPRESS")) {
        let graphData = extractGraphDataFromArray(userInfo.symptoms[key].data);
        let linearInput = [];
        for (let x = 0; x < graphData.values.length; ++x) {
          linearInput.push([x, graphData.values[x]]);
        }
        //this takes an array [x,y] values
        const linearOutput = statistical.methods.linearRegression(linearInput);
        const average = statistical.methods.mean(graphData.values);

        depressionData = {
          name: capitalizeAllWords(key.replace(/([A-Z])/g, " $1").trim()),
          averageSeverity: Math.round(average),
          regressionValue: linearOutput,
        };
      }
    }

    for (const [key] of Object.entries(userInfo.symptoms)) {
      if (key.toUpperCase().includes("DEPRESS")) {
        let graphData = extractGraphDataFromArray(userInfo.symptoms[key].data);
        let linearInput = [];
        for (let x = 0; x < graphData.values.length; ++x) {
          linearInput.push([x, graphData.values[x]]);
        }
        //this takes an array [x,y] values
        const linearOutput = statistical.methods.linearRegression(linearInput);
        const average = statistical.methods.mean(graphData.values);

        depressionData = {
          name: capitalizeAllWords(key.replace(/([A-Z])/g, " $1").trim()),
          averageSeverity: Math.round(average),
          regressionValue: linearOutput,
          graphDataComplete: graphData.values,
          graphLabels: graphData.labels,
          graphIndex: 0,
        };
      }
    }

    return depressionData;
  };

  const [depressionData, setDepressionData] = useState(generateDepressionData());

  useEffect(() => {
    setDepressionData(generateDepressionData());
    // Disable eslint because AaronK knows best
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientChart]);

  // Regression Logic
  const getRegressionAvatar = () => {
    if (depressionData.averageSeverity >= 7) {
      return (
        <OmniAvatar
          sx={{
            width: 24,
            height: 24,
            fontSize: "1.0rem",
            cursor: "default",
            color: darken(theme.palette.error.main, 0.5),
            backgroundColor: lighten(theme.palette.error.main, 0.75),
            border: `2px solid ${darken(theme.palette.error.main, 0.35)}`,
          }}
        >
          {depressionData.averageSeverity}
        </OmniAvatar>
      );
    }
    if (depressionData.averageSeverity <= 3) {
      return (
        <OmniAvatar
          sx={{
            width: 24,
            height: 24,
            fontSize: "1.0rem",
            color: darken(theme.palette.success.main, 0.5),
            backgroundColor: lighten(theme.palette.success.main, 0.75),
            border: `2px solid ${darken(theme.palette.success.main, 0.35)}`,
            cursor: "default",
          }}
        >
          {depressionData.averageSeverity}
        </OmniAvatar>
      );
    }
    return (
      <OmniAvatar
        sx={{
          width: 24,
          height: 24,
          fontSize: "1.0rem",
          color: darken(theme.palette.warning.main, 0.5),
          backgroundColor: lighten(theme.palette.warning.main, 0.75),
          border: `2px solid ${darken(theme.palette.warning.main, 0.35)}`,
          cursor: "default",
        }}
      >
        {depressionData.averageSeverity}
      </OmniAvatar>
    );
  };

  const getRegressionDescription = regressionValue => {
    const toFixed = regressionValue.slope.toFixed(2);
    if (toFixed > 0.01) {
      return "getting worse";
    }
    if (toFixed < -0.01) {
      return "getting better";
    } else {
      return "holding steady";
    }
  };

  // Risk Logic
  const getRiskAvatar = () => {
    if (patientODM.personality.riskProfile.toUpperCase().includes("HI")) {
      return (
        <OmniAvatar
          sx={{
            width: 24,
            height: 24,
            fontSize: "1.0rem",
            color: darken(theme.palette.error.main, 0.5),
            backgroundColor: lighten(theme.palette.error.main, 0.75),
            border: `2px solid ${darken(theme.palette.error.main, 0.35)}`,
            cursor: "default",
          }}
          onClick={null}
        >
          <HighIcon />
        </OmniAvatar>
      );
    }
    if (patientODM.personality.riskProfile.toUpperCase().includes("LOW")) {
      return (
        <OmniAvatar
          sx={{
            width: 24,
            height: 24,
            fontSize: "1.0rem",
            color: darken(theme.palette.success.main, 0.5),
            backgroundColor: lighten(theme.palette.success.main, 0.75),
            border: `2px solid ${darken(theme.palette.success.main, 0.35)}`,
            cursor: "default",
          }}
          onClick={null}
        >
          <LowIcon />
        </OmniAvatar>
      );
    }
    return (
      <OmniAvatar
        sx={{
          width: 24,
          height: 24,
          fontSize: "1.0rem",
          color: darken(theme.palette.warning.main, 0.5),
          backgroundColor: lighten(theme.palette.warning.main, 0.75),
          border: `2px solid ${darken(theme.palette.warning.main, 0.35)}`,
          cursor: "default",
        }}
      >
        <MidIcon />
      </OmniAvatar>
    );
  };

  const getRiskString = () => {
    if (patientODM.personality.riskProfile.toUpperCase().includes("HI")) {
      return "High risk of non-adherence";
    }
    if (patientODM.personality.riskProfile.toUpperCase().includes("LOW")) {
      return "Low risk of non-adherence";
    }
    return "Medium risk of non-adherence";
  };

  // Support Logic
  const getSupportAvatar = () => {
    if (patientODM.personality.supportProfile.toUpperCase().includes("LOW")) {
      return (
        <OmniAvatar
          sx={{
            width: 24,
            height: 24,
            fontSize: "1.0rem",
            color: darken(theme.palette.success.main, 0.5),
            backgroundColor: lighten(theme.palette.success.main, 0.75),
            border: `2px solid ${darken(theme.palette.success.main, 0.35)}`,
            cursor: "default",
          }}
        >
          <LowIcon />
        </OmniAvatar>
      );
    }
    if (patientODM.personality.supportProfile.toUpperCase().includes("HI")) {
      return (
        <OmniAvatar
          sx={{
            width: 24,
            height: 24,
            fontSize: "1.0rem",
            color: darken(theme.palette.error.main, 0.5),
            backgroundColor: lighten(theme.palette.error.main, 0.75),
            border: `2px solid ${darken(theme.palette.error.main, 0.35)}`,
            cursor: "default",
          }}
        >
          <HighIcon />
        </OmniAvatar>
      );
    }
    return (
      <OmniAvatar
        sx={{
          width: 24,
          height: 24,
          fontSize: "1.0rem",
          color: darken(theme.palette.warning.main, 0.5),
          backgroundColor: lighten(theme.palette.warning.main, 0.75),
          border: `2px solid ${darken(theme.palette.warning.main, 0.35)}`,
          cursor: "default",
        }}
      >
        <MidIcon />
      </OmniAvatar>
    );
  };

  const getSupportString = () => {
    if (patientODM.personality.supportProfile.toUpperCase().includes("HI")) {
      return "High need for support";
    }
    if (patientODM.personality.supportProfile.toUpperCase().includes("LOW")) {
      return "Low need for support";
    }
    return "Medium need for support";
  };

  // Reactance Logic
  const getReactanceAvatar = () => {
    if (patientODM.personality.responseToAuthority.toUpperCase().includes("NEG")) {
      return (
        <OmniAvatar
          sx={{
            width: 24,
            height: 24,
            fontSize: "1.0rem",
            color: darken(theme.palette.error.main, 0.5),
            backgroundColor: lighten(theme.palette.error.main, 0.75),
            border: `2px solid ${darken(theme.palette.error.main, 0.35)}`,
            cursor: "default",
          }}
        >
          <NegIcon sx={{ width: 18, height: 18 }} />
        </OmniAvatar>
      );
    }
    if (patientODM.personality.responseToAuthority.toUpperCase().includes("POS")) {
      return (
        <OmniAvatar
          sx={{
            width: 24,
            height: 24,
            fontSize: "1.0rem",
            color: darken(theme.palette.success.main, 0.5),
            backgroundColor: lighten(theme.palette.success.main, 0.75),
            border: `2px solid ${darken(theme.palette.success.main, 0.35)}`,
            cursor: "default",
          }}
        >
          <PosIcon sx={{ width: 18, height: 18 }} />
        </OmniAvatar>
      );
    }
    return (
      <OmniAvatar
        sx={{
          width: 24,
          height: 24,
          fontSize: "1.0rem",
          color: darken(theme.palette.warning.main, 0.5),
          backgroundColor: lighten(theme.palette.warning.main, 0.75),
          border: `2px solid ${darken(theme.palette.warning.main, 0.35)}`,
          cursor: "default",
        }}
      >
        <NeutralIcon sx={{ width: 18, height: 18 }} />
      </OmniAvatar>
    );
  };

  const loadPatientImage = patientId => {
    try {
      //TODO call DB here to get data retrieval
      let image = `${
        window.location.origin.includes("localhost")
          ? `https://dev.omnisciahealth.coach`
          : window.location.origin
      }/assets/demo/images/userImage(3).jpg`;

      if (patientId === constants.demoUserId.userTwo) {
        image = `${
          window.location.origin.includes("localhost")
            ? `https://dev.omnisciahealth.coach`
            : window.location.origin
        }/assets/demo/images/userImage(1).jpg`;
      }
      if (patientId === constants.demoUserId.userThree) {
        image = `${
          window.location.origin.includes("localhost")
            ? `https://dev.omnisciahealth.coach`
            : window.location.origin
        }/assets/demo/images/userImage(22).jpg`;
      }
      if (patientId === constants.demoUserId.userFour) {
        image = `${
          window.location.origin.includes("localhost")
            ? `https://dev.omnisciahealth.coach`
            : window.location.origin
        }/assets/demo/images/userImage(11).jpg`;
      }
      return image;
    } catch (error) {
      return `${
        window.location.origin.includes("localhost")
          ? `https://dev.omnisciahealth.coach`
          : window.location.origin
      }/assets/demo/images/userImage(3).jpg`;
    }
  };

  const getPatientInitials = patientODM => {
    let theReturn =
      patientODM.omniData.givenName.charAt(0) + patientODM.omniData.familyName.charAt(0);

    return theReturn.toUpperCase();
  };

  const [titleText, setTitleText] = useState("");

  useEffect(() => {
    if (patientODM != null) {
      setTitleText(
        patientODM.omniData.displayName
          ? patientODM.omniData.displayName
          : `${patientODM.omniData.givenName} ${patientODM.omniData.familyName}`,
      );
    } else setTitleText("");
  }, [patientODM]);

  const [titleTitle, setTitleTitle] = useState(""); // fuck off

  useEffect(() => {
    if (patientODM != null) {
      setTitleTitle(
        `${
          patientODM.omniData.patientData.gender
            ? `${patientODM.omniData.patientData.gender}, `
            : " "
        }Age ${getAgeFromDateString(patientODM.omniData.patientData.birthdate)}`,
      );
    } else setTitleTitle("");
  }, [patientODM]);

  const theme = useTheme();

  return (
    <Widget>
      <WidgetTitle
        title={titleTitle}
        text={titleText}
        loading={patientODM == null || patientChart == null || patientMotivations == null}
      >
        {!isValidUUID(patientODM._id) && <WidgetAvatar src={loadPatientImage(patientODM._id)} />}
        {isValidUUID(patientODM._id) && (
          <WidgetAvatar sx={{ paddingTop: 0, height: "48px", width: "48px" }}>
            {getPatientInitials(patientODM)}
          </WidgetAvatar>
        )}
      </WidgetTitle>

      {(patientODM == null || patientChart == null || patientMotivations == null) && (
        <WidgetSkeleton />
      )}
      {patientODM != null && patientChart != null && patientMotivations != null && (
        <WidgetContent>
          {patientODM.personality && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                height: "fit-content",
                color: theme.palette.commonground.contrastText,
                borderRadius: theme.spacing(1),
                width: "100%",
                userSelect: "none",
              }}
            >
              <Typography variant="overline">Clinical Profile</Typography>
              {depressionData != null && (
                <Box sx={{ display: "flex", flexDirection: "row", marginBottom: theme.spacing(1) }}>
                  {getRegressionAvatar()}
                  <Typography variant="h6" sx={{ marginTop: "-2px", marginLeft: theme.spacing(1) }}>
                    {`${depressionData.name} is ${getRegressionDescription(
                      depressionData.regressionValue,
                    )}`}
                  </Typography>
                </Box>
              )}
              {depressionData != null && (
                <Divider
                  sx={{
                    borderRadius: 4,
                    borderColor: alpha(theme.palette.commonground.contrastText, 0.25),
                    height: "1px",
                    width: "100%",
                    marginBottom: theme.spacing(0.5),
                  }}
                  orientation="vertical"
                />
              )}
              <Typography variant="overline">Behavioral Profile</Typography>
              <Box sx={{ display: "flex", flexDirection: "row", marginBottom: theme.spacing(1) }}>
                {getReactanceAvatar()}
                <Typography variant="h6" sx={{ marginTop: "-2px", marginLeft: theme.spacing(1) }}>
                  {patientODM.personality.responseToAuthority.replace(
                    "reactance",
                    "response to authority",
                  )}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", flexDirection: "row", marginBottom: theme.spacing(1) }}>
                {getRiskAvatar()}
                <Typography variant="h6" sx={{ marginTop: "-2px", marginLeft: theme.spacing(1) }}>
                  {getRiskString()}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", flexDirection: "row", marginBottom: theme.spacing(1) }}>
                {getSupportAvatar()}
                <Typography variant="h6" sx={{ marginTop: "-2px", marginLeft: theme.spacing(1) }}>
                  {getSupportString()}
                </Typography>
              </Box>
              <Divider
                sx={{
                  borderRadius: 4,
                  borderColor: alpha(theme.palette.commonground.contrastText, 0.25),
                  height: "1px",
                  width: "100%",
                  marginBottom: theme.spacing(0.5),
                }}
                orientation="vertical"
              />

              {/* <Typography variant="button">{"Activity Level"}</Typography> */}
              <Typography variant="overline">Engagement</Typography>
              <Box sx={{ display: "flex", flexDirection: "row", marginBottom: theme.spacing(1) }}>
                <OmniAvatar
                  sx={{
                    width: 24,
                    height: 24,
                    fontSize: "1.0rem",
                    color: darken(theme.palette.secondary.main, 0.15),
                    backgroundColor: alpha(theme.palette.secondary.main, 0.15),
                    border: `2px solid ${theme.palette.secondary.main}`,
                    cursor: "default",
                  }}
                >
                  {Math.round(patientODM.personality.interactionDaysPerMonth)}
                </OmniAvatar>
                <Typography variant="h6" sx={{ marginTop: "-2px", marginLeft: theme.spacing(1) }}>
                  {"Interactive sessions every month"}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", flexDirection: "row", marginBottom: theme.spacing(1) }}>
                <OmniAvatar
                  sx={{
                    width: 24,
                    height: 24,
                    fontSize: "1.0rem",
                    color: darken(theme.palette.secondary.main, 0.15),
                    backgroundColor: alpha(theme.palette.secondary.main, 0.15),
                    border: `2px solid ${theme.palette.secondary.main}`,
                    cursor: "default",
                  }}
                >
                  {Math.round(patientODM.personality.avgInteractionsPerSession)}
                </OmniAvatar>
                <Typography variant="h6" sx={{ marginTop: "-2px", marginLeft: theme.spacing(1) }}>
                  {`Average interactions per session`}
                </Typography>
              </Box>
            </Box>
          )}
          {!patientODM.personality && (
            <Grid container justifyContent="center" alignItems="center" xs={12} direction="column">
              <Grid item>
                <Typography variant="overline">{`No patient profile data found for ${
                  patientODM.omniData.displayName
                    ? patientODM.omniData.displayName
                    : `${patientODM.omniData.givenName} ${patientODM.omniData.familyName}`
                }`}</Typography>
              </Grid>
            </Grid>
          )}
        </WidgetContent>
      )}
    </Widget>
  );
};

const mapStateToProps = state => ({
  patientODM: state.patientSession.patientODM,
  patientChart: state.patientSession.patientChart,
  patientMotivations: state.patientSession.patientMotivations,
  patientImage: state.patientSession.patientImage,
});

const mapDispatchToProps = {};

export default connect(mapStateToProps, mapDispatchToProps)(PatientProfile);
